import Cookies from 'js-cookie';

// Set a value to a cookie with additional attributes
export function setCookie(key, value, options = {}) {
  Cookies.set(key, value, {
    expires: 60, // Cookie expiration in days
    path: '/', // Path attribute
    secure: true, // True for using HTTPS
    sameSite: 'None', // As needed ('Lax', 'Strict', or 'None')
    ...options
  });
}

// Get a value from a cookie
export function getCookie(key) {
  return Cookies.get(key);
}

// Remove a cookie with attributes
export function removeCookie(key, options = {}) {
  Cookies.remove(key, {
    path: '',
    ...options
  });
}

// Set specific values
export function setUserInfo({ firstName, email, token, id, accessLevel }, options = {}) {
  setCookie('first_name', firstName, options);
  setCookie('email', email, options);
  setCookie('token', token, options);
  setCookie('id', id, options);
  setCookie('access_level', accessLevel, options);
}

// Get all specific values
export function getUserInfo() {
  return {
    firstName: getCookie('first_name'),
    email: getCookie('email'),
    token: getCookie('token'),
    id: getCookie('id'),
    accessLevel: getCookie('access_level')
  };
}

export function canViewKnowledgeCitations() {
  const { id } = getUserInfo() || {};
  return String(id ?? '').trim() === '1';
}

// Clear all specific values
export function clearUserInfo(options = {}) {
  removeCookie('first_name', options);
  removeCookie('email', options);
  removeCookie('token', options);
  removeCookie('id', options);
  removeCookie('access_level', options);
}

// Set data type toggle value
export function setToggleDataTypeState({ chartToggleDataTypeValue }, options = {}) {
  setCookie('chart_toggle_data_type_value', chartToggleDataTypeValue, options);
}

// Get data type toggle value
export function getToggleDataTypeState() {
  return {
    chartToggleDataTypeValue: getCookie('chart_toggle_data_type_value'),
  };
}

// Set data grouping toggle value
export function setToggleGroupingState({ chartToggleGroupingValue }, options = {}) {
  setCookie('chart_toggle_grouping_value', chartToggleGroupingValue, options);
}

// Get data grouping toggle value
export function getToggleGroupingState() {
  return {
    chartToggleGroupingValue: getCookie('chart_toggle_grouping_value'),
  };
}

// Set specific value
export function setPowerChartLegendsState({powerHiddenLegends }, options = {}) {
  setCookie('power_hidden_legends', powerHiddenLegends, options);
}

// Set specific value
export function setPriceChartLegendsState({priceHiddenLegends }, options = {}) {
  setCookie('price_hidden_legends', priceHiddenLegends, options);
}


// Get value
export function getPowerChartLegendsState() {
  return {
    powerHiddenLegends: getCookie('power_hidden_legends'),
  };
}

// Get value
export function getPriceChartLegendsState() {
  return {
    priceHiddenLegends: getCookie('price_hidden_legends'),
  };
}


// Clear cookie data for power chart legends
export function clearPowerChartLegendsState(options = {}) {
  removeCookie('power_hidden_legends', options);
}

// Clear cookie data for power chart legends
export function clearPriceChartLegendsState(options = {}) {
  removeCookie('price_hidden_legends', options);
}

// Set dev mode state
export function setDevModeState({ isDevMode }, options = {}) {
  setCookie('dev_mode', isDevMode, options);
}

// Get dev mode state
export function getDevModeState() {
  return {
    isDevMode: getCookie('dev_mode') === 'true'
  };
}

// Set user preferences for chart visibility
export function setUserPreferences(preferences, options = {}) {
  setCookie('user_preferences', JSON.stringify(preferences), options);
}

// Get user preferences for chart visibility
export function getUserPreferences() {
  const preferences = getCookie('user_preferences');
  if (preferences) {
    try {
      return JSON.parse(preferences);
    } catch (e) {
      return getDefaultUserPreferences();
    }
  }
  return getDefaultUserPreferences();
}

// Default user preferences
export function getDefaultUserPreferences() {
  return {
    predictedBatteryActions: false,
    predictedBatteryActionsOracle: false,
    pricePrediction: true,
    pvPredictionNonAdjusted: true,
    loadPredictionLowerBound: false,
    loadPredictionUpperBound: false,
    predictedLoadHourly: false,
    priceActualBuySellRX: false,
    predictedGridPower: false,
    actionReasons: false,
    bmsSimulator: false,
    showSeparateSocChart: false,
    showDeviceSoHComparison: false
  };
}

// Clear user preferences
export function clearUserPreferences(options = {}) {
  removeCookie('user_preferences', options);
}

// Set detail page version (legacy or birdsEye)
export function setDetailPageVersion({ version }, options = {}) {
  setCookie('detail_page_version', version, options);
}

// Get detail page version
export function getDetailPageVersion() {
  const version = getCookie('detail_page_version');
  return version || 'birdsEye'; // Default to birdsEye
}

// Set prediction evaluation visibility state
export function setPredictionEvaluationState({ isEnabled }, options = {}) {
  setCookie('prediction_evaluation_enabled', isEnabled, options);
}

// Get prediction evaluation visibility state
export function getPredictionEvaluationState() {
  const state = getCookie('prediction_evaluation_enabled');
  // Default to true (visible) if no cookie value exists
  if (state === null || state === undefined || state === '') {
    return {
      isEnabled: true
    };
  }
  return {
    isEnabled: state === 'true' || state === true
  };
}

// Set dark mode state
export function setDarkModeState({ isDarkMode }, options = {}) {
  setCookie('dark_mode', isDarkMode, options);
}

// Get dark mode state
export function getDarkModeState() {
  const state = getCookie('dark_mode');
  // Return null if no preference is saved (to detect system preference)
  if (state === null || state === undefined || state === '') {
    return null;
  }
  return state === 'true' || state === true;
}

// Set power chart log scale state
export function setPowerChartLogScaleState({ isLogScale }, options = {}) {
  setCookie('power_chart_log_scale', isLogScale, options);
}

// Get power chart log scale state
export function getPowerChartLogScaleState() {
  const state = getCookie('power_chart_log_scale');
  return {
    isLogScale: state === 'true' || state === true
  };
}

// Set price chart log scale state
export function setPriceChartLogScaleState({ isLogScale }, options = {}) {
  setCookie('price_chart_log_scale', isLogScale, options);
}

// Get price chart log scale state
export function getPriceChartLogScaleState() {
  const state = getCookie('price_chart_log_scale');
  return {
    isLogScale: state === 'true' || state === true
  };
}

// Set SoC chart log scale state
export function setSocChartLogScaleState({ isLogScale }, options = {}) {
  setCookie('soc_chart_log_scale', isLogScale, options);
}

// Get SoC chart log scale state
export function getSocChartLogScaleState() {
  const state = getCookie('soc_chart_log_scale');
  return {
    isLogScale: state === 'true' || state === true
  };
}



